import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { translations } from "../utils/translations";

const LanguageContext = createContext(null);

const STORAGE_KEY = "tl_storefront_lang";

export function LanguageProvider({ children }) {
  // Storefront language defaults to 'ar' (Arabic)
  const [language, setLanguageState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === "en" ? "en" : "ar";
    } catch {
      return "ar";
    }
  });

  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isRTL = language === "ar";

  // Synchronize document direction and language code based on current route
  useEffect(() => {
    if (isAdminRoute) {
      // Admin panels must remain in English / LTR
      document.documentElement.dir = "ltr";
      document.documentElement.lang = "en";
      document.body.classList.remove("rtl-storefront");
      document.body.classList.add("ltr-admin");
    } else {
      // Storefront uses the selected language (Arabic by default)
      document.documentElement.dir = isRTL ? "rtl" : "ltr";
      document.documentElement.lang = language;
      if (isRTL) {
        document.body.classList.add("rtl-storefront");
        document.body.classList.remove("ltr-admin");
      } else {
        document.body.classList.remove("rtl-storefront");
        document.body.classList.remove("ltr-admin");
      }
    }
  }, [language, isRTL, isAdminRoute]);

  const setLanguage = useCallback((newLang) => {
    const valid = newLang === "en" ? "en" : "ar";
    setLanguageState(valid);
    try {
      localStorage.setItem(STORAGE_KEY, valid);
    } catch {
      // storage unavailable or restricted
    }
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage(language === "ar" ? "en" : "ar");
  }, [language, setLanguage]);

  // Translation lookup helper
  const t = useCallback(
    (key, params = {}) => {
      const dict = translations[language] || translations.ar;
      let text = dict[key] || translations.en?.[key] || key;

      if (params && typeof params === "object") {
        Object.keys(params).forEach((paramKey) => {
          text = text.replace(new RegExp(`\\{${paramKey}\\}`, "g"), params[paramKey]);
        });
      }
      return text;
    },
    [language]
  );

  return (
    <LanguageContext.Provider
      value={{
        language,
        isRTL,
        setLanguage,
        toggleLanguage,
        t,
        isAdminRoute,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
